import { AxiosResponse } from 'axios';
import { PAGE_SIZE } from '~/constants';
import {
  EquipmentType,
  EquipmentTypeNamePreview,
  EquipmentTypeView,
  Pagination,
  Parametros,
  SelectItem,
} from '~/types';
import api from '~/utils/api';
import { formatInTimeZone } from '~/utils/formatToTimeZone';

export async function getEquipmentTypes(
  page: number,
  size: number,
): Promise<Pagination<EquipmentTypeView>> {
  const { data } = await api.get<Pagination<EquipmentType>>(
    '/tipoequipamento',
    {
      params: {
        page,
        size,
      },
    },
  );

  const equipmentTypes = data.results.map(type => ({
    ...type,
    status: type.tp_situacao === '1',
    formattedInitialDate: formatInTimeZone(
      type.dh_ultimaatualizacao,
      'dd/MM/yyyy',
    ),
    formattedStatus: type.tp_situacao === '1' ? 'ATIVO' : 'INATIVO',
    colorScheme: type.tp_situacao === '1' ? 'green' : 'red',
  }));

  return {
    ...data,
    results: equipmentTypes,
  };
}

export async function updateEquipmentTypeById(
  id: string,
  formData: any,
): Promise<AxiosResponse<any>> {
  return api.put(`/tipoequipamento/${id}`, formData);
}

export async function createEquipmentType(
  formData: any,
): Promise<AxiosResponse<any>> {
  return api.post(`/tipoequipamento`, formData);
}

export async function getEquipmentTypesAsSelect(): Promise<SelectItem[]> {
  const responses: SelectItem[] = [];

  return new Promise((resolve, rejects) => {
    async function getData(page: number) {
      return api
        .get<Pagination<EquipmentType>>('/tipoequipamento', {
          params: {
            page,
            size: PAGE_SIZE,
          },
        })
        .then(response => {
          const options = response.data.results
            .filter(type => type.tp_situacao === '1')
            .map(type => ({
              label: type.nm_tipoequipamento || '-',
              value: String(type.id_tipoequipamento),
            }));
          responses.push(...options);

          if (response?.data.next) {
            getData(page + 1);
            return response;
          }
          return resolve(responses);
        })
        .catch(() => {
          return rejects(responses);
        });
    }
    getData(1);
  });
}

export async function getEquipmentTypesPreview(
  parametros: Parametros[],
  id_tipoequipamento: number,
): Promise<EquipmentTypeNamePreview> {
  const parametrosFiltrados = parametros
    .filter(parametro => parametro.status)
    .map(parametro => parametro.nome)
    .map(param => `parametros=${param}`)
    .join('&');

  const url = parametrosFiltrados
    ? `/tipoequipamento/${id_tipoequipamento}/get_preview?${parametrosFiltrados}`
    : `/tipoequipamento/${id_tipoequipamento}/get_preview`;

  const { data } = await api.get<EquipmentTypeNamePreview>(url);
  return data;
}

export async function getEquipmentTypePreview(
  id_equipamento: number,
): Promise<string> {
  const url = `/equipamentos/${id_equipamento}/reset_descricao`;

  const { data } = await api.get<string>(url);
  return data;
}

export async function getEquipmentTypeByModelId(
  id_modeloequipamento: number | undefined,
): Promise<EquipmentType> {
  if (!id_modeloequipamento) {
    return {} as EquipmentType;
  }
  const url = `/modeloequipamento/${id_modeloequipamento}/tipo_equipamento`;

  const { data } = await api.get<EquipmentType>(url);
  return data;
}

export async function getEquipmentDescriptionByType(
  id_equipamento: number,
  id_tipoequipamento: number,
): Promise<string> {
  const url = `/equipamentos/${id_equipamento}/descricao_por_tipo_equipamento?id_tipoequipamento=${id_tipoequipamento}`;

  const { data } = await api.get<string>(url);
  return data;
}
